import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useStudentAuth } from '@/context/StudentAuthContext';
import {
  ArrowLeft,
  ArrowRight,
  Calculator,
  GraduationCap,
  Users,
  Building2,
  CreditCard,
  FileCheck,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Info,
  Phone,
  MessageCircle,
  ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// High Commission Norms - Living Expenses (Annual in USD)
const LIVING_EXPENSES = {
  'USA': { min: 15000, max: 20000, currency: 'USD', display: '$15,000 - $20,000' },
  'UK': { min: 15000, max: 18000, currency: 'GBP', display: '£12,000 - £15,000' },
  'Canada': { min: 12000, max: 15000, currency: 'CAD', display: 'CAD 15,000 - 20,000' },
  'Australia': { min: 14000, max: 17000, currency: 'AUD', display: 'AUD 21,000 - 25,000' },
  'Germany': { min: 11000, max: 13000, currency: 'EUR', display: '€10,000 - €12,000' },
  'New Zealand': { min: 12000, max: 15000, currency: 'NZD', display: 'NZD 18,000 - 22,000' },
  'Ireland': { min: 10000, max: 12000, currency: 'EUR', display: '€9,000 - €11,000' },
  'France': { min: 10000, max: 12000, currency: 'EUR', display: '€9,000 - €11,000' },
  'Netherlands': { min: 12000, max: 14000, currency: 'EUR', display: '€11,000 - €13,000' },
  'Singapore': { min: 10000, max: 14000, currency: 'SGD', display: 'SGD 14,000 - 18,000' },
  'Dubai': { min: 8000, max: 12000, currency: 'AED', display: 'AED 30,000 - 45,000' }
};

// Average Tuition Fees (1st Year in USD)
const TUITION_FEES = {
  'USA': { min: 30000, max: 55000, average: 42000 },
  'UK': { min: 18000, max: 35000, average: 25000 },
  'Canada': { min: 15000, max: 35000, average: 22000 },
  'Australia': { min: 20000, max: 45000, average: 30000 },
  'Germany': { min: 500, max: 20000, average: 5000 },
  'New Zealand': { min: 15000, max: 30000, average: 22000 },
  'Ireland': { min: 12000, max: 30000, average: 18000 },
  'France': { min: 3000, max: 20000, average: 8000 },
  'Netherlands': { min: 10000, max: 25000, average: 15000 },
  'Singapore': { min: 15000, max: 40000, average: 25000 },
  'Dubai': { min: 12000, max: 30000, average: 20000 }
};

const INCOME_RANGES = [
  { value: 'below_5', label: 'Below ₹5 Lakhs', score: 1 },
  { value: '5_10', label: '₹5 - 10 Lakhs', score: 2 },
  { value: '10_20', label: '₹10 - 20 Lakhs', score: 3 },
  { value: '20_50', label: '₹20 - 50 Lakhs', score: 4 },
  { value: 'above_50', label: 'Above ₹50 Lakhs', score: 5 }
];

const CREDIT_SCORE_RANGES = [
  { value: 'excellent', label: '750+ (Excellent)', score: 5 },
  { value: 'good', label: '700-749 (Good)', score: 4 },
  { value: 'fair', label: '650-699 (Fair)', score: 3 },
  { value: 'poor', label: 'Below 650 (Poor)', score: 1 },
  { value: 'no_history', label: 'No Credit History', score: 2 },
  { value: 'unknown', label: "Don't Know", score: 2 }
];

const PROPERTY_VALUES = [
  { value: 'none', label: 'No Property', score: 0 },
  { value: 'below_50', label: 'Below ₹50 Lakhs', score: 2 },
  { value: '50_1cr', label: '₹50 Lakhs - 1 Crore', score: 3 },
  { value: '1cr_2cr', label: '₹1 - 2 Crore', score: 4 },
  { value: 'above_2cr', label: 'Above ₹2 Crore', score: 5 }
];

const steps = [
  { id: 1, title: 'Study Details', icon: GraduationCap },
  { id: 2, title: 'Co-Applicant', icon: Users },
  { id: 3, title: 'Collateral', icon: Building2 },
  { id: 4, title: 'Credit Profile', icon: CreditCard },
  { id: 5, title: 'Results', icon: FileCheck }
];

const LoanEligibilityChecker = () => {
  const { isDark } = useTheme();
  const { isAuthenticated, loading: authLoading } = useStudentAuth();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const [formData, setFormData] = useState({
    // Study Details
    country: '',
    tuitionEstimate: '',
    admissionStatus: '',
    
    // Co-Applicant
    hasCoApplicant: '',
    coApplicantRelation: '',
    coApplicantEmployment: '',
    annualIncome: '',
    
    // Collateral
    hasProperty: '',
    propertyType: '',
    propertyValue: '',
    
    // Credit Profile
    studentCreditScore: '',
    coApplicantCreditScore: '',
    existingEMI: '',
    existingEMIAmount: ''
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/uac-desk/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateEligibility = () => {
    setLoading(true);
    
    setTimeout(() => {
      // Calculate loan amount needed
      const country = formData.country;
      const tuition = TUITION_FEES[country]?.average || 25000;
      const living = LIVING_EXPENSES[country]?.max || 15000;
      const totalFirstYear = tuition + living;
      const totalInINR = Math.round(totalFirstYear * 83); // Approx USD to INR
      
      // Calculate eligibility score (out of 100)
      let score = 0;
      let factors = [];
      
      // Income factor (max 25 points)
      const incomeData = INCOME_RANGES.find(i => i.value === formData.annualIncome);
      const incomeScore = (incomeData?.score || 2) * 5;
      score += incomeScore;
      factors.push({ 
        name: 'Family Income', 
        score: incomeScore, 
        max: 25,
        status: incomeScore >= 15 ? 'positive' : incomeScore >= 10 ? 'neutral' : 'negative'
      });
      
      // Collateral factor (max 25 points)
      const propertyData = PROPERTY_VALUES.find(p => p.value === formData.propertyValue);
      const collateralScore = (propertyData?.score || 0) * 5;
      score += collateralScore;
      factors.push({ 
        name: 'Collateral Security', 
        score: collateralScore, 
        max: 25,
        status: collateralScore >= 15 ? 'positive' : collateralScore >= 10 ? 'neutral' : 'negative'
      });
      
      // Credit score factor (max 25 points)
      const creditData = CREDIT_SCORE_RANGES.find(c => c.value === formData.coApplicantCreditScore);
      const creditScore = (creditData?.score || 2) * 5;
      score += creditScore;
      factors.push({ 
        name: 'Credit Profile', 
        score: creditScore, 
        max: 25,
        status: creditScore >= 20 ? 'positive' : creditScore >= 15 ? 'neutral' : 'negative'
      });
      
      // Co-applicant & Employment factor (max 15 points)
      let coAppScore = 0;
      if (formData.hasCoApplicant === 'yes') {
        coAppScore += 5;
        if (formData.coApplicantEmployment === 'salaried') coAppScore += 10;
        else if (formData.coApplicantEmployment === 'self_employed') coAppScore += 7;
        else if (formData.coApplicantEmployment === 'retired_pension') coAppScore += 5;
        else coAppScore += 2;
      }
      score += coAppScore;
      factors.push({ 
        name: 'Co-Applicant Profile', 
        score: coAppScore, 
        max: 15,
        status: coAppScore >= 12 ? 'positive' : coAppScore >= 7 ? 'neutral' : 'negative'
      });
      
      // Admission status factor (max 10 points)
      let admissionScore = 0;
      if (formData.admissionStatus === 'offer_received') admissionScore = 10;
      else if (formData.admissionStatus === 'applied') admissionScore = 5;
      else admissionScore = 3;
      score += admissionScore;
      factors.push({ 
        name: 'Admission Status', 
        score: admissionScore, 
        max: 10,
        status: admissionScore >= 8 ? 'positive' : admissionScore >= 5 ? 'neutral' : 'negative'
      });
      
      // Existing EMI impact (deduction)
      if (formData.existingEMI === 'yes') {
        const emiAmount = parseInt(formData.existingEMIAmount) || 0;
        if (emiAmount > 50000) score -= 10;
        else if (emiAmount > 25000) score -= 5;
      }
      
      // Determine eligibility level
      let eligibility = 'low';
      let eligibilityLabel = 'Low';
      let eligibilityColor = 'text-red-500';
      let estimatedLoanPercent = 50;
      
      if (score >= 75) {
        eligibility = 'high';
        eligibilityLabel = 'High';
        eligibilityColor = 'text-green-500';
        estimatedLoanPercent = 90;
      } else if (score >= 55) {
        eligibility = 'medium';
        eligibilityLabel = 'Medium';
        eligibilityColor = 'text-yellow-500';
        estimatedLoanPercent = 75;
      }
      
      const estimatedLoanAmount = Math.round((totalInINR * estimatedLoanPercent) / 100);
      
      setResults({
        score,
        eligibility,
        eligibilityLabel,
        eligibilityColor,
        factors,
        country,
        tuitionUSD: tuition,
        livingUSD: living,
        totalFirstYearUSD: totalFirstYear,
        totalFirstYearINR: totalInINR,
        estimatedLoanINR: estimatedLoanAmount,
        estimatedLoanPercent
      });
      
      setCurrentStep(5);
      setLoading(false);
    }, 1500);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.country && formData.admissionStatus;
      case 2:
        if (formData.hasCoApplicant === 'no') return true;
        return formData.hasCoApplicant && formData.coApplicantRelation && 
               formData.coApplicantEmployment && formData.annualIncome;
      case 3:
        if (formData.hasProperty === 'no') return true;
        return formData.hasProperty && formData.propertyValue;
      case 4:
        return formData.coApplicantCreditScore;
      default:
        return true;
    }
  };

  if (authLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-ux-slate-light'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
      </div>
    );
  }

  // Results View
  if (currentStep === 5 && results) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#1a2830]' : 'bg-ux-slate-light'}`}>
        {/* Header */}
        <header className={`sticky top-0 z-50 border-b ${isDark ? 'bg-[#233842] border-white/10' : 'bg-white border-ux-slate-light'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Button variant="ghost" onClick={() => navigate('/uac-desk/dashboard')} data-testid="back-to-dashboard">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <Button variant="outline" onClick={() => { setCurrentStep(1); setResults(null); }} data-testid="check-again-btn">
                Check Again
              </Button>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Eligibility Result Card */}
          <div className={`glass-panel rounded-3xl p-8 mb-6 ${isDark ? '' : 'bg-white shadow-lg'}`} data-testid="eligibility-result">
            <div className="text-center mb-8">
              <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${
                results.eligibility === 'high' ? 'bg-green-500/20' :
                results.eligibility === 'medium' ? 'bg-yellow-500/20' : 'bg-red-500/20'
              }`}>
                {results.eligibility === 'high' ? (
                  <CheckCircle2 className="w-10 h-10 text-green-500" />
                ) : results.eligibility === 'medium' ? (
                  <AlertTriangle className="w-10 h-10 text-yellow-500" />
                ) : (
                  <AlertTriangle className="w-10 h-10 text-red-500" />
                )}
              </div>
              <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Loan Eligibility: <span className={results.eligibilityColor}>{results.eligibilityLabel}</span>
              </h2>
              <p className={`${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                Based on the information provided
              </p>
            </div>

            {/* Loan Estimate */}
            <div className={`rounded-2xl p-6 mb-6 ${isDark ? 'bg-white/5' : 'bg-ux-slate-light/50'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Estimated Loan Details for {results.country}
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                  <p className={`text-sm ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>1st Year Tuition (Est.)</p>
                  <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    ${results.tuitionUSD.toLocaleString()}
                  </p>
                </div>
                <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                  <p className={`text-sm ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>Living Expenses (Est.)</p>
                  <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    ${results.livingUSD.toLocaleString()}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>
                    As per High Commission norms
                  </p>
                </div>
              </div>

              <div className={`p-4 rounded-xl border-2 border-dashed ${isDark ? 'border-white/20' : 'border-ux-charcoal/20'}`}>
                <div className="flex justify-between items-center mb-2">
                  <p className={`${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>Total 1st Year Requirement</p>
                  <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    ${results.totalFirstYearUSD.toLocaleString()} (~₹{(results.totalFirstYearINR / 100000).toFixed(1)}L)
                  </p>
                </div>
                <div className="flex justify-between items-center">
                  <p className={`${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>Estimated Loan You May Qualify For</p>
                  <p className="text-2xl font-bold text-ux-cyan">
                    ₹{(results.estimatedLoanINR / 100000).toFixed(1)} Lakhs
                  </p>
                </div>
                <p className={`text-xs mt-2 ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>
                  ~{results.estimatedLoanPercent}% of total requirement based on your profile
                </p>
              </div>
            </div>

            {/* Factor Breakdown */}
            <div className="mb-6">
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Assessment Breakdown
              </h3>
              <div className="space-y-3">
                {results.factors.map((factor, idx) => (
                  <div key={idx} className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                    <div className="flex justify-between items-center mb-2">
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        {factor.name}
                      </span>
                      <span className={`text-sm font-medium ${
                        factor.status === 'positive' ? 'text-green-500' :
                        factor.status === 'neutral' ? 'text-yellow-500' : 'text-red-500'
                      }`}>
                        {factor.score}/{factor.max}
                      </span>
                    </div>
                    <div className={`h-2 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                      <div 
                        className={`h-full rounded-full transition-all ${
                          factor.status === 'positive' ? 'bg-green-500' :
                          factor.status === 'neutral' ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${(factor.score / factor.max) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Disclaimer */}
            <div className={`p-4 rounded-xl border ${isDark ? 'border-yellow-500/30 bg-yellow-500/10' : 'border-yellow-500/50 bg-yellow-50'}`}>
              <div className="flex gap-3">
                <Info className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className={`text-sm font-medium mb-1 ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>
                    Important Disclaimer
                  </p>
                  <p className={`text-xs ${isDark ? 'text-yellow-400/70' : 'text-yellow-600'}`}>
                    This assessment is indicative only and does not guarantee loan approval. Actual eligibility depends on 
                    lender-specific criteria, documentation, credit bureau reports, and prevailing policies. Final loan 
                    amount, interest rates, and terms will be determined by the financial institution after complete 
                    evaluation of your application.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA - Connect with Counsellor */}
          <div className={`glass-panel rounded-2xl p-6 ${isDark ? '' : 'bg-white shadow-lg'}`}>
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
              Ready to Take the Next Step?
            </h3>
            <p className={`mb-4 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
              Our education loan experts can help you navigate the loan process, find the best rates, and maximize your approval chances.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                className="flex-1 bg-gradient-to-r from-ux-cyan to-ux-blue"
                onClick={() => window.open('tel:+917273928928', '_self')}
                data-testid="call-counsellor-btn"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call a Counsellor
              </Button>
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => window.open('https://wa.me/917273928928?text=Hi, I just completed the loan eligibility check on your portal and would like to discuss my education loan options.', '_blank')}
                data-testid="whatsapp-counsellor-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp Us
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Form View
  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#1a2830]' : 'bg-ux-slate-light'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b ${isDark ? 'bg-[#233842] border-white/10' : 'bg-white border-ux-slate-light'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" onClick={() => navigate('/uac-desk/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-ux-cyan" />
              <span className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Education Loan Eligibility
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className={`border-b ${isDark ? 'border-white/10' : 'border-ux-slate-light'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {steps.slice(0, 4).map((step, index) => (
              <div 
                key={step.id}
                className={`flex items-center ${index < 3 ? 'flex-1' : ''}`}
              >
                <div className={`flex items-center gap-2 ${
                  currentStep >= step.id 
                    ? 'text-ux-cyan' 
                    : isDark ? 'text-white/30' : 'text-ux-charcoal/30'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= step.id 
                      ? 'bg-ux-cyan text-white' 
                      : isDark ? 'bg-white/10 text-white/30' : 'bg-ux-slate-light text-ux-charcoal/30'
                  }`}>
                    {currentStep > step.id ? '✓' : step.id}
                  </div>
                  <span className="hidden md:block text-sm font-medium">{step.title}</span>
                </div>
                {index < 3 && (
                  <div className={`flex-1 h-0.5 mx-4 ${
                    currentStep > step.id 
                      ? 'bg-ux-cyan' 
                      : isDark ? 'bg-white/10' : 'bg-ux-slate-light'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className={`glass-panel rounded-3xl p-8 ${isDark ? '' : 'bg-white shadow-lg'}`}>
          
          {/* Step 1: Study Details */}
          {currentStep === 1 && (
            <div className="space-y-6" data-testid="step-1">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Where do you plan to study?
              </h2>
              
              <div>
                <Label className={isDark ? 'text-white/70' : ''}>Country of Study <span className="text-red-400">*</span></Label>
                <Select value={formData.country} onValueChange={(v) => updateField('country', v)}>
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`} data-testid="country-select">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(LIVING_EXPENSES).map(country => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.country && (
                <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-ux-slate-light/50'}`}>
                  <p className={`text-sm ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                    Estimated 1st Year Costs for {formData.country}
                  </p>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <p className={`text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>Tuition (Avg)</p>
                      <p className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        ${TUITION_FEES[formData.country]?.average.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>Living Expenses</p>
                      <p className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        {LIVING_EXPENSES[formData.country]?.display}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label className={isDark ? 'text-white/70' : ''}>Custom Tuition Amount (Optional)</Label>
                <Input
                  type="number"
                  placeholder="Enter if you know exact tuition in USD"
                  value={formData.tuitionEstimate}
                  onChange={(e) => updateField('tuitionEstimate', e.target.value)}
                  className={`mt-1 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`}
                  data-testid="tuition-input"
                />
              </div>

              <div>
                <Label className={isDark ? 'text-white/70' : ''}>Admission Status <span className="text-red-400">*</span></Label>
                <Select value={formData.admissionStatus} onValueChange={(v) => updateField('admissionStatus', v)}>
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`} data-testid="admission-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="offer_received">Offer Letter Received</SelectItem>
                    <SelectItem value="applied">Applied, Awaiting Decision</SelectItem>
                    <SelectItem value="planning">Planning to Apply</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 2: Co-Applicant */}
          {currentStep === 2 && (
            <div className="space-y-6" data-testid="step-2">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Co-Applicant Details
              </h2>
              <p className={`${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                Most education loans require a co-applicant (parent/guardian)
              </p>
              
              <div>
                <Label className={isDark ? 'text-white/70' : ''}>Do you have a Co-Applicant? <span className="text-red-400">*</span></Label>
                <div className="flex gap-3 mt-2">
                  <Button
                    type="button"
                    variant={formData.hasCoApplicant === 'yes' ? 'default' : 'outline'}
                    onClick={() => updateField('hasCoApplicant', 'yes')}
                    className={formData.hasCoApplicant === 'yes' ? 'bg-ux-cyan' : ''}
                    data-testid="co-applicant-yes"
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={formData.hasCoApplicant === 'no' ? 'default' : 'outline'}
                    onClick={() => updateField('hasCoApplicant', 'no')}
                    className={formData.hasCoApplicant === 'no' ? 'bg-ux-cyan' : ''}
                    data-testid="co-applicant-no"
                  >
                    No
                  </Button>
                </div>
              </div>

              {formData.hasCoApplicant === 'yes' && (
                <>
                  <div>
                    <Label className={isDark ? 'text-white/70' : ''}>Relationship <span className="text-red-400">*</span></Label>
                    <Select value={formData.coApplicantRelation} onValueChange={(v) => updateField('coApplicantRelation', v)}>
                      <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`}>
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="parent">Parent</SelectItem>
                        <SelectItem value="guardian">Guardian</SelectItem>
                        <SelectItem value="spouse">Spouse</SelectItem>
                        <SelectItem value="sibling">Sibling</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className={isDark ? 'text-white/70' : ''}>Employment Type <span className="text-red-400">*</span></Label>
                    <Select value={formData.coApplicantEmployment} onValueChange={(v) => updateField('coApplicantEmployment', v)}>
                      <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`} data-testid="employment-select">
                        <SelectValue placeholder="Select employment type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="salaried">Salaried Employee</SelectItem>
                        <SelectItem value="self_employed">Self Employed / Business</SelectItem>
                        <SelectItem value="retired_pension">Retired with Pension</SelectItem>
                        <SelectItem value="retired_no_pension">Retired without Pension</SelectItem>
                        <SelectItem value="homemaker">Homemaker</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className={isDark ? 'text-white/70' : ''}>Annual Family Income <span className="text-red-400">*</span></Label>
                    <Select value={formData.annualIncome} onValueChange={(v) => updateField('annualIncome', v)}>
                      <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`} data-testid="income-select">
                        <SelectValue placeholder="Select income range" />
                      </SelectTrigger>
                      <SelectContent>
                        {INCOME_RANGES.map(range => (
                          <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {formData.hasCoApplicant === 'no' && (
                <div className={`p-4 rounded-xl border ${isDark ? 'border-yellow-500/30 bg-yellow-500/10' : 'border-yellow-500/50 bg-yellow-50'}`}>
                  <div className="flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                    <p className={`text-sm ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>
                      Most lenders require a co-applicant for education loans. Without one, loan options may be limited to unsecured loans with higher interest rates.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Collateral */}
          {currentStep === 3 && (
            <div className="space-y-6" data-testid="step-3">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Collateral Security
              </h2>
              <p className={`${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                Property collateral can help secure larger loan amounts at better rates
              </p>
              
              <div>
                <Label className={isDark ? 'text-white/70' : ''}>Do you have property for collateral? <span className="text-red-400">*</span></Label>
                <div className="flex gap-3 mt-2">
                  <Button
                    type="button"
                    variant={formData.hasProperty === 'yes' ? 'default' : 'outline'}
                    onClick={() => updateField('hasProperty', 'yes')}
                    className={formData.hasProperty === 'yes' ? 'bg-ux-cyan' : ''}
                    data-testid="property-yes"
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={formData.hasProperty === 'no' ? 'default' : 'outline'}
                    onClick={() => updateField('hasProperty', 'no')}
                    className={formData.hasProperty === 'no' ? 'bg-ux-cyan' : ''}
                    data-testid="property-no"
                  >
                    No
                  </Button>
                </div>
              </div>

              {formData.hasProperty === 'yes' && (
                <>
                  <div>
                    <Label className={isDark ? 'text-white/70' : ''}>Property Type</Label>
                    <Select value={formData.propertyType} onValueChange={(v) => updateField('propertyType', v)}>
                      <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`}>
                        <SelectValue placeholder="Select property type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential">Residential Property</SelectItem>
                        <SelectItem value="commercial">Commercial Property</SelectItem>
                        <SelectItem value="land">Land / Plot</SelectItem>
                        <SelectItem value="fd">Fixed Deposits / Bonds</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className={isDark ? 'text-white/70' : ''}>Estimated Property Value <span className="text-red-400">*</span></Label>
                    <Select value={formData.propertyValue} onValueChange={(v) => updateField('propertyValue', v)}>
                      <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`} data-testid="property-value-select">
                        <SelectValue placeholder="Select value range" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROPERTY_VALUES.filter(p => p.value !== 'none').map(prop => (
                          <SelectItem key={prop.value} value={prop.value}>{prop.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {formData.hasProperty === 'no' && (
                <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-ux-slate-light/50'}`}>
                  <p className={`text-sm ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                    You can still apply for unsecured education loans (typically up to ₹7.5 Lakhs without collateral). 
                    For higher amounts, collateral is usually required.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Credit Profile */}
          {currentStep === 4 && (
            <div className="space-y-6" data-testid="step-4">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Credit Profile
              </h2>
              
              <div>
                <Label className={isDark ? 'text-white/70' : ''}>Student's Credit Score</Label>
                <Select value={formData.studentCreditScore} onValueChange={(v) => updateField('studentCreditScore', v)}>
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`}>
                    <SelectValue placeholder="Select credit score range" />
                  </SelectTrigger>
                  <SelectContent>
                    {CREDIT_SCORE_RANGES.map(score => (
                      <SelectItem key={score.value} value={score.value}>{score.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>
                  Check your CIBIL score at myscore.cibil.com
                </p>
              </div>

              <div>
                <Label className={isDark ? 'text-white/70' : ''}>Co-Applicant's Credit Score <span className="text-red-400">*</span></Label>
                <Select value={formData.coApplicantCreditScore} onValueChange={(v) => updateField('coApplicantCreditScore', v)}>
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`} data-testid="credit-score-select">
                    <SelectValue placeholder="Select credit score range" />
                  </SelectTrigger>
                  <SelectContent>
                    {CREDIT_SCORE_RANGES.map(score => (
                      <SelectItem key={score.value} value={score.value}>{score.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className={isDark ? 'text-white/70' : ''}>Any Existing EMIs/Loans?</Label>
                <div className="flex gap-3 mt-2">
                  <Button
                    type="button"
                    variant={formData.existingEMI === 'yes' ? 'default' : 'outline'}
                    onClick={() => updateField('existingEMI', 'yes')}
                    className={formData.existingEMI === 'yes' ? 'bg-ux-cyan' : ''}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={formData.existingEMI === 'no' ? 'default' : 'outline'}
                    onClick={() => updateField('existingEMI', 'no')}
                    className={formData.existingEMI === 'no' ? 'bg-ux-cyan' : ''}
                  >
                    No
                  </Button>
                </div>
              </div>

              {formData.existingEMI === 'yes' && (
                <div>
                  <Label className={isDark ? 'text-white/70' : ''}>Total Monthly EMI Amount (₹)</Label>
                  <Input
                    type="number"
                    placeholder="Enter total monthly EMI"
                    value={formData.existingEMIAmount}
                    onChange={(e) => updateField('existingEMIAmount', e.target.value)}
                    className={`mt-1 ${isDark ? 'bg-white/5 border-white/20 text-white' : ''}`}
                  />
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(s => s - 1)}
              disabled={currentStep === 1}
              className={isDark ? 'border-white/20' : ''}
              data-testid="prev-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>

            {currentStep < 4 ? (
              <Button
                onClick={() => setCurrentStep(s => s + 1)}
                disabled={!canProceed()}
                className="bg-gradient-to-r from-ux-cyan to-ux-blue"
                data-testid="next-btn"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={calculateEligibility}
                disabled={loading || !canProceed()}
                className="bg-gradient-to-r from-ux-violet to-ux-rose"
                data-testid="check-eligibility-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    <Calculator className="w-4 h-4 mr-2" />
                    Check Eligibility
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoanEligibilityChecker;
